import React, { useState, useRef, useEffect } from 'react';
import { apiFetch } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../../CentralSnackbar';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  IconButton,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon, Add as AddIcon, UploadFile as UploadFileIcon, Refresh as RefreshIcon, Download as DownloadIcon, MedicalServicesOutlined as ExaminationsIcon } from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:25000';

const Examinations = () => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [examinations, setExaminations] = useState([]);
  const [search, setSearch] = useState('');
  const [modalityFilter, setModalityFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [groupBy, setGroupBy] = useState('region');
  const [loading, setLoading] = useState(false);
  const [deletingLoading, setDeletingLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEditExam, setDialogEditExam] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const fileInputRef = useRef();

  // Formular-States für Add/Edit Dialog
  const [formData, setFormData] = useState({
    short_code: '',
    name: '',
    region: '',
    modality: '',
    is_active: true
  });

  // Reset form data when dialog opens/closes
  const resetFormData = () => {
    setFormData({
      short_code: '',
      name: '',
      region: '',
      modality: '',
      is_active: true
    });
  };

  useEffect(() => {
    fetchExaminations();
    // eslint-disable-next-line
  }, []);

  // Lade Untersuchungen vom Backend
  const fetchExaminations = async () => {
    setLoading(true);
    try {
      const res = await apiFetch('/admin/examinations');
      if (res.ok) {
        let data = await res.json();
        // Standardsortierung: erst modality, dann name (beides a->z)
        data = data.sort((a, b) => {
          if ((a.modality || '').toLowerCase() < (b.modality || '').toLowerCase()) return -1;
          if ((a.modality || '').toLowerCase() > (b.modality || '').toLowerCase()) return 1;
          if ((a.name || '').toLowerCase() < (b.name || '').toLowerCase()) return -1;
          if ((a.name || '').toLowerCase() > (b.name || '').toLowerCase()) return 1;
          return 0;
        });
        setExaminations(data);
      }
    } catch (e) {
      showError(t('admin.reportingManagement.examinations.loadError'), 'ERR-EXAM-001');
    } finally {
      setLoading(false);
    }
  };

  // --- UI-RENDER ---
  const hasSelected = selected.length > 0;
  const exactlyOneSelected = selected.length === 1;

  // Verfügbare Modalitäten und Regionen aus den VOLLEN Daten extrahieren (nicht gefiltert)
  const availableModalities = [...new Set(examinations.map(e => e.modality).filter(Boolean))].sort();
  const availableRegions = [...new Set(examinations.map(e => e.region).filter(Boolean))].sort();

  // Client-seitige Filterung
  const filteredExaminations = examinations.filter(exam => {
    if (modalityFilter && exam.modality !== modalityFilter) return false;
    if (regionFilter && exam.region !== regionFilter) return false;
    if (search) {
      const searchLower = search.toLowerCase();
      const matchesName = (exam.name || '').toLowerCase().includes(searchLower);
      const matchesShortCode = (exam.short_code || '').toLowerCase().includes(searchLower);
      const matchesModality = (exam.modality || '').toLowerCase().includes(searchLower);
      const matchesRegion = (exam.region || '').toLowerCase().includes(searchLower);
      if (!matchesName && !matchesShortCode && !matchesModality && !matchesRegion) return false;
    }
    return true;
  });

  // Checkbox-Handler
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(filteredExaminations.map(exam => exam.id));
    } else {
      setSelected([]);
    }
  };

  const handleSelect = (examId) => {
    setSelected(prev => 
      prev.includes(examId) 
        ? prev.filter(id => id !== examId)
        : [...prev, examId]
    );
  };

  // Button-Handler
  const handleOpenAdd = () => {
    resetFormData();
    setDialogEditExam(null);
    setDialogOpen(true);
  };

  const handleOpenEdit = () => {
    const selectedExam = examinations.find(exam => exam.id === selected[0]);
    setDialogEditExam(selectedExam);
    setFormData({
      short_code: selectedExam.short_code || '',
      name: selectedExam.name || '',
      region: selectedExam.region || '',
      modality: selectedExam.modality || '',
      is_active: selectedExam.is_active !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = () => {
    if (selected.length === 1) {
      const selectedExam = examinations.find(exam => exam.id === selected[0]);
      setDeleteTarget(selectedExam);
    } else {
      setDeleteTarget({ count: selected.length });
    }
    setDeleteDialogOpen(true);
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      handleCsvUpload(file);
    }
  };

  const handleCsvUpload = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
      setLoading(true);

      const response = await fetch(`${API_BASE}/admin/examinations/upload-csv`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders()
        },
        body: formData
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Upload result:', result);
        console.log('Result.imported type:', typeof result.imported);
        console.log('Result.imported value:', result.imported);
        
        // Sicherheitskorrektur falls result.imported ein Array ist
        const count = Array.isArray(result.imported) ? result.imported.length : result.imported;
        
        const message = count > 0 
          ? t('admin.reportingManagement.examinations.uploadSuccess', { count: count })
          : t('admin.reportingManagement.examinations.uploadNoNew');
        showSuccess(message);
        
        fetchExaminations();
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.examinations.uploadError'), 'ERR-EXAM-002');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.examinations.uploadError'), 'ERR-EXAM-003');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      const url = dialogEditExam 
        ? `${API_BASE}/admin/examinations/${dialogEditExam.id}`
        : `${API_BASE}/admin/examinations`;
      
      const method = dialogEditExam ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setDialogOpen(false);
        showSuccess(dialogEditExam ? t('common.saveSuccess') : t('common.createSuccess'));
        fetchExaminations();
        setSelected([]);
      } else {
        const errorData = await response.json();
        showError(errorData.detail || t('admin.reportingManagement.examinations.saveError'), 'ERR-EXAM-004');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.examinations.saveError'), 'ERR-EXAM-005');
    }
  };

  const handleCsvTemplateDownload = () => {
    // CSV Header definieren
    const headers = [
      'Shortcut',
      'Description', 
      'Region',
      'Method'
    ];

    // Deutsche Header für Benutzerfreundlichkeit
    const germanHeaders = [
      'Kürzel',
      'Beschreibung',
      'Region',
      'Modalität'
    ];

    // CSV Inhalt erstellen mit BOM für korrekte Darstellung in Excel
    const csvContent = '\uFEFF' + [
      headers.join(';'), // Technische Header (werden vom Backend verarbeitet)
      '# ' + germanHeaders.join(';'), // Deutsche Header als Kommentarzeile
      '# Beispiel: CT-THORAX;CT Thorax Routineprotokoll;Thorax;CT',
      '# Vorhandene Untersuchungen (gleicher Kürzel) werden automatisch übersprungen',
      ''
    ].join('\n');

    // CSV Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'untersuchungen_vorlage.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleConfirmDelete = async () => {
    try {
      setDeletingLoading(true);
      
      let deletedCount = 0;
      let hasErrors = false;

      // Lösche alle ausgewählten Untersuchungen einzeln
      for (const examId of selected) {
        try {
          const response = await fetch(`${API_BASE}/admin/examinations/${examId}`, {
            method: 'DELETE',
            headers: {
              ...getAuthHeaders()
            }
          });

          if (response.ok) {
            deletedCount++;
          } else {
            hasErrors = true;
          }
        } catch {
          hasErrors = true;
        }
      }

      if (deletedCount > 0) {
        setDeleteDialogOpen(false);
        if (selected.length === 1) {
          showSuccess(t('common.deleteSuccess'));
        } else {
          showSuccess(t('admin.reportingManagement.examinations.deleteMultipleSuccess', { count: deletedCount }));
        }
        fetchExaminations();
        setSelected([]);
      }

      if (hasErrors) {
        showError(t('admin.reportingManagement.examinations.deletePartialSuccess', { 
          success: deletedCount, 
          total: selected.length 
        }), 'ERR-EXAM-006');
      }
    } catch (error) {
      showError(t('admin.reportingManagement.examinations.deleteError'), 'ERR-EXAM-007');
    } finally {
      setDeletingLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ExaminationsIcon color="primary" />
          {t('admin.reportingManagement.examinations.title')}
        </Typography>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAdd}
            className="button"
            disabled={loading || deletingLoading}
          >
            {t('common.add')}
          </Button>
          <Button
            className="button"
            variant="contained"
            startIcon={<UploadFileIcon />}
            component="label"
            disabled={loading || deletingLoading}
          >
            {t('admin.reportingManagement.examinations.uploadCsv')}
            <input
              type="file"
              accept=".csv"
              hidden
              ref={fileInputRef}
              onChange={handleFileChange}
            />
          </Button>
          <Button
            className="button"
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleCsvTemplateDownload}
            disabled={loading || deletingLoading}
          >
            {t('admin.reportingManagement.examinations.csvTemplate')}
          </Button>
        </Box>
      </Box>

      {/* Aktionsleiste */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="subtitle2" sx={{ mr: 2 }}>
              {hasSelected
                ? t('admin.reportingManagement.examinations.selected', { count: selected.length })
                : t('admin.reportingManagement.examinations.noSelected')
              }
            </Typography>
            <Divider orientation="vertical" flexItem />
            <Tooltip title={t('common.edit')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleOpenEdit}
                  disabled={!exactlyOneSelected || loading || deletingLoading}
                >
                  <EditIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('common.delete')}>
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={handleDelete}
                  disabled={!hasSelected || loading || deletingLoading}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          
          {/* Filter-Bereich */}
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('admin.reportingManagement.examinations.modality')}</InputLabel>
              <Select
                value={modalityFilter}
                label={t('admin.reportingManagement.examinations.modality')}
                onChange={(e) => setModalityFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {availableModalities.map((modality) => (
                  <MenuItem key={modality} value={modality}>
                    {modality}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('admin.reportingManagement.examinations.region')}</InputLabel>
              <Select
                value={regionFilter}
                label={t('admin.reportingManagement.examinations.region')}
                onChange={(e) => setRegionFilter(e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('common.all')}</em>
                </MenuItem>
                {availableRegions.map((region) => (
                  <MenuItem key={region} value={region}>
                    {region}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              label={t('admin.reportingManagement.examinations.search')}
              value={search}
              onChange={e => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 220 }}
            />
          </Box>
        </Box>
      </Paper>

      {/* Tabelle */}
      <Paper sx={{ width: '100%' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 420px)' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < filteredExaminations.length}
                    checked={filteredExaminations.length > 0 && selected.length === filteredExaminations.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.examinations.short_code')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.examinations.name')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.examinations.region')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.examinations.modality')}</TableCell>
                <TableCell sx={{ position: 'sticky', top: 0, backgroundColor: (theme) => theme.palette.background.paper, zIndex: 2 }}>{t('admin.reportingManagement.examinations.is_active')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredExaminations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    {t('admin.reportingManagement.examinations.noResults')}
                  </TableCell>
                </TableRow>
              ) : (
                filteredExaminations.map(exam => (
                  <TableRow key={exam.id} selected={selected.includes(exam.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selected.includes(exam.id)}
                        onChange={() => handleSelect(exam.id)}
                      />
                    </TableCell>
                    <TableCell>{exam.short_code}</TableCell>
                    <TableCell>{exam.name}</TableCell>
                    <TableCell>{exam.region}</TableCell>
                    <TableCell>{exam.modality}</TableCell>
                    <TableCell>{exam.is_active ? t('common.active') : t('common.inactive')}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogEditExam ? t('admin.reportingManagement.examinations.editTitle') : t('admin.reportingManagement.examinations.addTitle')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {dialogEditExam ? t('admin.reportingManagement.examinations.description') : t('admin.reportingManagement.examinations.description')}
          </DialogContentText>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label={t('admin.reportingManagement.examinations.short_code')}
              value={formData.short_code}
              onChange={(e) => setFormData({ ...formData, short_code: e.target.value })}
              fullWidth
              required
              size="small"
            />
            
            <TextField
              label={t('admin.reportingManagement.examinations.name')}
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              size="small"
            />
            
            <TextField
              label={t('admin.reportingManagement.examinations.region')}
              value={formData.region}
              onChange={(e) => setFormData({ ...formData, region: e.target.value })}
              fullWidth
              size="small"
            />
            
            <TextField
              label={t('admin.reportingManagement.examinations.modality')}
              value={formData.modality}
              onChange={(e) => setFormData({ ...formData, modality: e.target.value })}
              fullWidth
              size="small"
            />
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Checkbox
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                size="small"
              />
              <Typography variant="body2">
                {t('admin.reportingManagement.examinations.is_active')}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} className="button-cancel">
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            className="button"
            disabled={!formData.short_code || !formData.name}
          >
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !deletingLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>
          {t('common.delete')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {selected.length === 1 
              ? (() => {
                  const exam = examinations.find(e => e.id === selected[0]);
                  return t('admin.reportingManagement.examinations.deleteConfirmSingle', { 
                    name: exam?.name || exam?.short_code || 'Untersuchung'
                  });
                })()
              : t('admin.reportingManagement.examinations.deleteConfirmMultiple', { count: selected.length })
            }
          </DialogContentText>
          {deletingLoading && (
            <Box display="flex" alignItems="center" gap={2} mt={2}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                {t('common.deleting')}...
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)} 
            className="button-cancel"
            disabled={deletingLoading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleConfirmDelete} 
            className="button-delete"
            disabled={deletingLoading}
          >
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Examinations; 